import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import styles from './ArticlesList.module.css';
import cx from 'classnames';
import {api, ApiRoutes} from '../../../../services/ApiService';
import {OnboardingArticle} from '../../../../types/Article';
import {Merkmal, SelectedMerkmal} from '../../../../types/Merkmal';
import Pagination from '../../../../components/Pagination';
import Input from '../../../../components/Input';
import MerkmaleFilter from '../MerkmaleFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useOnboardingContext } from '../../../../contexts/OnboardingContext';
import { branchCategoryIdsKey } from '../../../../helpers/branchCategoryHelper';
import { openModal, closeModal } from '../../../../services/ModalService';
import SelectedArticlesModal from './SelectedArticlesModal';

interface ArticlesListProps {
    categoryId: number | null;
    branchCategoryIds?: number[];
    herstellerIds?: number[];
    isActive?: boolean;
    selectedArticles?: OnboardingArticle[];
    selectedMerkmale?: SelectedMerkmal[];
    availableMerkmale?: Merkmal[];
    onArticleSelect?: (article: OnboardingArticle) => void;
    onMerkmaleLoaded?: (merkmale: Merkmal[]) => void;
    onMerkmaleChange?: (merkmale: SelectedMerkmal[]) => void;
}

const ArticlesList: React.FC<ArticlesListProps> = ({
    categoryId,
    branchCategoryIds = [],
    herstellerIds = [],
    isActive = false,
    selectedArticles = [],
    selectedMerkmale = [],
    availableMerkmale = [],
    onArticleSelect,
    onMerkmaleLoaded,
    onMerkmaleChange
}) => {
    const { formData } = useOnboardingContext();
    const packageLimit = 0;
    const isUnlimited = true;
    const [articles, setArticles] = useState<OnboardingArticle[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pagination, setPagination] = useState<{
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    } | null>(null);
    const [, setMerkmale] = useState<Merkmal[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const prevCategoryIdRef = useRef<number | null>(null);
    const prevMerkmaleRef = useRef<SelectedMerkmal[]>([]);
    const articlesHeaderRef = useRef<HTMLDivElement>(null);
    const prevPageRef = useRef<number>(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const herstellerIdsKey = useMemo(
        () => [...herstellerIds].sort((a, b) => a - b).join(','),
        [herstellerIds]
    );

    const branchCategoriesKey = useMemo(
        () => branchCategoryIdsKey(branchCategoryIds),
        [branchCategoryIds]
    );

    const filterKey = useMemo(() => {
        return `${categoryId || 'null'}_${branchCategoriesKey || 'null'}_${herstellerIdsKey}_${selectedMerkmale.map(m => `${m.kMerkmal}-${m.kMerkmalWert}`).join(',')}`;
    }, [categoryId, branchCategoriesKey, herstellerIdsKey, selectedMerkmale]);

    useEffect(() => {
        if (!isActive) {
            setArticles([]);
            setPagination(null);
            setCurrentPage(1);
            setMerkmale([]);
            setSearchTerm('');
            setDebouncedSearchTerm('');
            return;
        }

        const fetchArticles = async () => {
            try {
                setLoading(true);
                const queryParams: Record<string, string> = {
                    page: currentPage.toString(),
                    limit: '15'
                };

                if (categoryId) {
                    queryParams.categoryId = categoryId.toString();
                }

                if (branchCategoryIds.length > 0) {
                    queryParams.branchCategoryIds = branchCategoriesKey;
                }

                if (herstellerIds.length > 0) {
                    queryParams.herstellerIds = [...herstellerIds]
                        .filter((id) => id > 0)
                        .sort((a, b) => a - b)
                        .join(',');
                }

                if (debouncedSearchTerm.trim()) {
                    queryParams.search = debouncedSearchTerm.trim();
                }

                if (selectedMerkmale.length > 0) {
                    queryParams.merkmale = selectedMerkmale
                        .map(m => `${m.kMerkmal}-${m.kMerkmalWert}`)
                        .join(',');
                }
                
                const response = await api.get(ApiRoutes.ARTICLES, undefined, queryParams);
                if (response.data?.data?.articles) {
                    setArticles(response.data.data.articles);
                }
                if (response.data?.data?.merkmale) {
                    setMerkmale(response.data.data.merkmale);
                    if (onMerkmaleLoaded) {
                        onMerkmaleLoaded(response.data.data.merkmale);
                    }
                }
                if (response.data?.pagination) {
                    setPagination(response.data.pagination);
                }
            } catch (error) {
                console.error('Failed to fetch articles:', error);
                setArticles([]);
                setPagination(null);
            } finally {
                setLoading(false);
            }
        };

        fetchArticles();
     
    }, [filterKey, isActive, currentPage, debouncedSearchTerm]);

    useEffect(() => {
        const categoryChanged = prevCategoryIdRef.current !== categoryId;
        const merkmaleChanged = JSON.stringify(prevMerkmaleRef.current) !== JSON.stringify(selectedMerkmale);
        
        if (categoryChanged) {
            prevCategoryIdRef.current = categoryId;
        }
        
        if (categoryChanged || merkmaleChanged) {
            if (currentPage !== 1) {
                setCurrentPage(1);
            }
            prevMerkmaleRef.current = selectedMerkmale;
        }
    }, [categoryId, herstellerIdsKey, selectedMerkmale, currentPage]);

    useEffect(() => {
        if (prevPageRef.current !== currentPage && articlesHeaderRef.current) {
            articlesHeaderRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
            });
            prevPageRef.current = currentPage;
        }
    }, [currentPage]);

    const handleSearchChange = useCallback((property: string, value: string) => {
        setSearchTerm(value);
    }, []);

    const placeholderImageUrl = useMemo(() => {
        const apiUrl = api.getBaseUrl();
        return `${apiUrl}storage/placeholder/tsoutfit.png`;
    }, []);

    const openSelectedArticlesModal = useCallback(() => {
        const modalIdRef = { current: '' };
        modalIdRef.current = openModal({
            content: (
                <SelectedArticlesModal onClose={() => closeModal(modalIdRef.current)} />
            ),
            size: 'md',
            onClose: () => closeModal(modalIdRef.current),
        });
    }, []);

    if (!isActive) {
        return null;
    }

    const formatPrice = (price: number): string => {
        if (price === 0) {
            return 'kostenlos';
        }
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    };

    const handleArticleClick = (article: OnboardingArticle) => {
        if (onArticleSelect) {
            onArticleSelect(article);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <div className={cx(styles['articles-list-container'])}>
            <div ref={articlesHeaderRef} className={cx(styles['articles-header'])}>
                <h3 className={cx(styles['articles-list-title'])}>
                    Artikel {pagination ? `(${pagination.total})` : `(${articles.length})`}
                </h3>
                {formData.selectedArticles.length > 0 && (
                    <div className={cx(styles['articles-limit'])}>
                        {isUnlimited ? (
                            <button
                                type="button"
                                className={cx(styles['limit-text'])}
                                onClick={openSelectedArticlesModal}
                            >
                                {selectedArticles.length} ausgewählt (unbegrenzt)
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className={cx(
                                        styles['limit-text'],
                                        selectedArticles.length >= packageLimit && styles['limit-reached']
                                    )}
                                    onClick={openSelectedArticlesModal}
                                >
                                    {selectedArticles.length} / {packageLimit} ausgewählt
                                </button>
                                {selectedArticles.length >= packageLimit && (
                                    <span className={cx(styles['limit-message'])}>
                                        Bitte upgraden Sie Ihr Paket für mehr Artikel
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className={cx(styles['articles-search'])}>
                <div className={cx(styles['search-filter-row'])}>
                    <Input
                        type="text"
                        placeholder="Artikel suchen..."
                        property="search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        classes={cx(styles['search-input'])}
                    />
                    {availableMerkmale.length > 0 && (
                        <div className={cx(styles['desktop-merkmale-filter'])}>
                            <MerkmaleFilter
                                merkmale={availableMerkmale}
                                isActive={isActive}
                                selectedMerkmale={selectedMerkmale}
                                onMerkmaleChange={onMerkmaleChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {loading ? (
                <div className={cx(styles['articles-grid'])}>
                    {Array.from({ length: 8 }, (_, i) => (
                        <div
                            key={i}
                            className={cx(styles['article-card'], styles['article-skeleton'])}
                        >
                            <div className={cx(styles['article-card-content'])}>
                                <div className={cx(styles['article-image-container'], styles['skeleton-block'])} />
                                <div className={cx(styles['skeleton-line'], styles['skeleton-block'])} />
                                <div className={cx(styles['skeleton-price'], styles['skeleton-block'])} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : articles.length === 0 ? (
                <div className={cx(styles['articles-list-empty'])}>
                    {debouncedSearchTerm.trim() 
                        ? 'Keine Artikel gefunden.' 
                        : selectedMerkmale.length > 0 
                            ? (
                                <>
                                    <p>Keine Artikel mit den gewählten Filtern gefunden.</p>
                                    <button 
                                        type="button"
                                        className={cx(styles['clear-filters-btn'])}
                                        onClick={() => onMerkmaleChange && onMerkmaleChange([])}
                                    >
                                        Filter zurücksetzen
                                    </button>
                                </>
                            )
                            : 'Keine Artikel in dieser Kategorie verfügbar.'}
                </div>
            ) : (
                <>
                    <div className={cx(styles['articles-grid'])}>
                        {articles.map((article) => {
                            const isSelected = selectedArticles.some(a => a.id === article.id);
                            return (
                                <div
                                    key={article.id}
                                    className={cx(
                                        styles['article-card'],
                                        isSelected && styles['selected']
                                    )}
                                    onClick={() => handleArticleClick(article)}
                                >
                                    <div className={cx(styles['article-card-content'])}>
                                        {isSelected && (
                                            <div className={cx(styles['article-checkmark'])}>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <circle cx="12" cy="12" r="12" fill="#D8232A"/>
                                                    <path d="M7 12L10 15L17 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>
                                        )}
                                        <a 
                                            href={article.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cx(styles['article-link-icon'])}
                                            onClick={(e) => e.stopPropagation()}
                                            title={`Zum Artikel: ${article.name}`}
                                        >
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                        </a>
                                        <div className={cx(styles['article-image-container'])}>
                                            <img 
                                                src={article.imageUrl || placeholderImageUrl} 
                                                alt={article.name}
                                                className={cx(styles['article-image'])}
                                            />
                                        </div>
                                        <h4 className={cx(styles['article-name'])}>
                                            {article.name}
                                        </h4>
                                        <div className={cx(styles['article-price'])}>
                                            {formatPrice(article.price)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {pagination && pagination.totalPages > 1 && (
                        <div className={cx(styles['articles-pagination'])}>
                            <Pagination
                                totalPages={pagination.totalPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ArticlesList;
